import { useLayoutEffect } from "react";

import { columnAnchorDomId } from "../utils/impactPages";

const ACTIVE_BOOK_SCROLLER = "[data-impact-book-scroll='active']";
const PRESIDENT_INNER_SCROLL = "[data-impact-thank-inner-scroll]";

function applySheetHeight(el: HTMLElement, height: number) {
  el.style.height = `${height}px`;
  el.style.minHeight = `${height}px`;
  el.style.maxHeight = `${height}px`;
}

/** Scroll up → AOL page; scroll down → president page (full viewport each). */
export function scrollImpactThankYouSubpage(index: 0 | 1) {
  const scroller = document.querySelector<HTMLElement>(ACTIVE_BOOK_SCROLLER);
  if (!scroller) return false;

  const height = scroller.clientHeight;
  if (height <= 0) return false;

  scroller.scrollTo({
    top: index * height,
    behavior: "smooth",
  });
  return true;
}

export function useImpactThankYouSubpageSnap(
  topColumnId: string,
  bottomColumnId: string,
  enabled = true
) {
  useLayoutEffect(() => {
    if (!enabled) return;

    const scroller = document.querySelector<HTMLElement>(ACTIVE_BOOK_SCROLLER);
    if (!scroller) return;

    const topSheet = document.getElementById(columnAnchorDomId(topColumnId));
    if (!topSheet || !scroller.contains(topSheet)) return;

    let pageHeight = 0;
    let lastScrollTop = scroller.scrollTop;
    let gestureDir: "up" | "down" | null = null;
    let snapTimer: number | undefined;
    let snapping = false;

    const sheetElements = () => [
      topSheet,
      document.getElementById(columnAnchorDomId(bottomColumnId)),
    ];

    const syncSheetHeights = () => {
      pageHeight = scroller.clientHeight;
      if (pageHeight <= 0) return;

      for (const el of sheetElements()) {
        if (el) applySheetHeight(el, pageHeight);
      }
    };

    const resolveTarget = (): number => {
      if (pageHeight <= 0) return 0;

      const y = scroller.scrollTop;
      const inner = scroller.querySelector<HTMLElement>(PRESIDENT_INNER_SCROLL);
      const onPresidentPage = y >= pageHeight * 0.5;

      if (onPresidentPage && inner && inner.scrollTop > 0) {
        return pageHeight;
      }

      if (gestureDir === "down") {
        return y >= pageHeight * 0.18 ? pageHeight : 0;
      }

      if (gestureDir === "up") {
        // Ignore scroll-end bounce: only return to AOL sheet when clearly in the top page.
        return y <= pageHeight * 0.38 ? 0 : pageHeight;
      }

      return y >= pageHeight * 0.5 ? pageHeight : 0;
    };

    const snap = (behavior: ScrollBehavior = "smooth") => {
      if (snapping || pageHeight <= 0) return;

      const target = resolveTarget();
      if (Math.abs(scroller.scrollTop - target) <= 1) {
        gestureDir = null;
        return;
      }

      snapping = true;
      scroller.scrollTo({ top: target, behavior });
      window.setTimeout(() => {
        snapping = false;
        gestureDir = null;
      }, behavior === "smooth" ? 320 : 0);
    };

    const onScroll = () => {
      if (snapping) return;

      const current = scroller.scrollTop;
      if (current > lastScrollTop + 6) {
        gestureDir = "down";
      } else if (current < lastScrollTop - 6) {
        gestureDir = "up";
      }
      lastScrollTop = current;

      window.clearTimeout(snapTimer);
      snapTimer = window.setTimeout(() => snap("smooth"), 90);
    };

    const onScrollEnd = () => {
      snap("smooth");
    };

    syncSheetHeights();

    const resizeObserver = new ResizeObserver(() => {
      syncSheetHeights();
    });
    resizeObserver.observe(scroller);

    scroller.addEventListener("scroll", onScroll, { passive: true });
    scroller.addEventListener("scrollend", onScrollEnd);

    const kick = window.setTimeout(syncSheetHeights, 0);

    return () => {
      window.clearTimeout(kick);
      window.clearTimeout(snapTimer);
      resizeObserver.disconnect();
      scroller.removeEventListener("scroll", onScroll);
      scroller.removeEventListener("scrollend", onScrollEnd);
    };
  }, [topColumnId, bottomColumnId, enabled]);
}
